import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
// The viewer's zone offset travels with the request so the server reads "today"
// as the viewer's day — the technician "active today" test is a local-day test,
// and without the offset a California district's day would end mid-afternoon.
import { clientZoneOffsetMinutes } from "@/components/map-review/columns";
import type { HomePayload } from "@/type/api";

/**
 * The Home screen's payload (GET /api/home).
 *
 * The screen is read whole and served by one request: the boundary, the weather
 * and wind over the district, and every technician's last-known position. A
 * failed request is a page-level failure — the caller shows it and keeps the
 * placeholder sections usable. The payload's own `unavailable` list is NOT an
 * error: the sections it names are simply absent, and the view reports them
 * without taking the rest of the screen down.
 */
export const useStoreHome = defineStore("home", () => {
	// State
	const loading = ref(false);
	const error = ref<string | null>(null);
	const payload = ref<HomePayload | null>(null);
	// Memoize the in-flight request so concurrent callers (the view mounting
	// twice under a re-navigated route, a caller that gets ahead of the fetch)
	// share one request instead of each issuing their own.
	const ongoingFetch = ref<Promise<HomePayload> | null>(null);

	// Prefer the message the API sent over axios's generic "Request failed with
	// status code N", which tells an operator nothing about what to do. The
	// response body is read by narrowing, not by asserting a shape onto it: an
	// assertion would read the field whatever the body actually was.
	function apiErrorMessage(err: unknown, fallback: string): string {
		if (err instanceof Error && "response" in err) {
			const response: unknown = err.response;
			if (response && typeof response === "object" && "data" in response) {
				const { data } = response;
				if (data && typeof data === "object" && "message" in data) {
					const { message } = data;
					if (typeof message === "string" && message !== "") return message;
				}
			}
		}
		if (err instanceof Error && err.message) return err.message;
		return fallback;
	}

	async function load(): Promise<HomePayload> {
		loading.value = true;
		error.value = null;
		try {
			const data = await apiClient.JSONGet<HomePayload>("/api/home", {
				params: { tz_offset_minutes: clientZoneOffsetMinutes() },
			});
			payload.value = data;
			return data;
		} catch (e) {
			// The previous payload is deliberately left in place: a failed
			// refresh must not blank a screen that was already readable.
			error.value = apiErrorMessage(
				e,
				"Could not load the district's situation.",
			);
			throw e;
		} finally {
			loading.value = false;
		}
	}

	/**
	 * fetchHome loads the payload. Concurrent callers share the one in-flight
	 * request; the memo is cleared when it settles, so a later call refetches.
	 * Rejects on failure, with the message on `error`.
	 */
	function fetchHome(): Promise<HomePayload> {
		const inFlight = ongoingFetch.value;
		if (inFlight !== null) {
			return inFlight;
		}
		const request = load().finally(() => {
			ongoingFetch.value = null;
		});
		ongoingFetch.value = request;
		return request;
	}

	return {
		// State
		error,
		loading,
		payload,
		// Actions
		fetchHome,
	};
});
